import { validationData, validationMoney } from "../../assets/validations/validationRegex";
import { useMedia } from "../../context/MediaContext";
import { useSubVetForm } from "../../context/SubVetFormContext";
import { Practica } from "../../interfaces/forms/Practica";
import {
  checkAndResetMedia,
  createBase64ArrayFromMedia,
  setFormValuesFromObject,
  submitSvf,
} from "../../utils/Form";
import { capitalizeFirstLetter } from "../../utils/General";
import AddingSvfBtns from "../AddingSvfBtns";
import FileUploader from "../FileUploader";
import FormInput from "../FormInput";
import FormSelectPopover from "../FormSelectPopover";
import Title from "../Title";
import { PracticasFormProps } from "./types";
import { IonAlert, IonCol, IonGrid, IonRow } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

const PracticasForm: React.FC<PracticasFormProps> = ({
  formattedPracticas,
  index,
  setAddingSvfElement,
  setShowToast,
}) => {
  const { practicas, setPracticas } = useSubVetForm();
  const { currentMedia, setCurrentMedia, setShowNoFileInCurrentMediaToast } = useMedia();
  const { handleSubmit, control, errors, setValue, watch } = useForm<Practica>();
  const formMethods = { control, errors };
  const practica: string | undefined = watch("practica");
  const [showNoEvidenceAlert, setShowNoEvidenceAlert] = useState(false);
  const [pendingValues, setPendingValues] = useState<Practica | undefined>();

  const savePractica = async (values: Practica) => {
    values.practicaId = String(formattedPracticas.indexOf(values.practica!) + 1);

    if (currentMedia) {
      const informe = await createBase64ArrayFromMedia(currentMedia);
      values.media = currentMedia;
      values.practicaInforme = informe;
    }

    submitSvf(index, practicas, setPracticas, values, setShowToast);
  };

  const onSubmit = async (values: Practica) => {
    if (!currentMedia?.length) {
      setPendingValues(values);
      setShowNoEvidenceAlert(true);
      return;
    }

    await savePractica(values);
  };

  const saveWithoutEvidence = async () => {
    if (!pendingValues) {
      return;
    }

    setShowNoEvidenceAlert(false);
    await savePractica(pendingValues);
    setPendingValues(undefined);
  };

  useEffect(() => {
    if (index !== undefined) {
      setFormValuesFromObject(practicas[index], setValue);
      setCurrentMedia(practicas[index].media!);
    }
  }, [index]);

  //Reset currentMedia cuando hay media cargada con un tipo de estudio especifico
  useEffect(() => {
    checkAndResetMedia(practica, currentMedia, setCurrentMedia, setShowNoFileInCurrentMediaToast);
  }, [practica, currentMedia]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <IonGrid className="ion-no-padding">
        <Title title={index !== undefined ? "Editar practica" : "Nueva practica"} />

        <IonRow>
          <IonCol hidden>
            {/* Practica Id*/}
            <FormInput
              formMethods={formMethods}
              fieldProps={{
                name: "practicaId",
                label: "",
              }}
            />
          </IonCol>
          <IonCol>
            {/* Practica */}
            <FormSelectPopover
              formMethods={formMethods}
              options={formattedPracticas}
              fieldProps={{
                name: "practica",
                label: "Práctica realizada",
              }}
              validationProps={{
                errorMessage: "Por favor seleccione una práctica",
              }}
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            {/* Descripción */}
            <FormInput
              formMethods={formMethods}
              fieldProps={{
                name: "descripcion",
                label: "Descripción de la práctica",
                format: capitalizeFirstLetter,
              }}
              validationProps={{
                pattern: validationData,
                maxLength: { value: 255, message: "Máximo 255 caracteres" },
              }}
            />
          </IonCol>
        </IonRow>
        <IonRow>
          <IonCol>
            {/* Costo */}
            <FormInput
              formMethods={formMethods}
              fieldProps={{
                name: "costo",
                label: "Costo de la práctica",
                typeNumber: true,
                currency: true,
              }}
              validationProps={{
                errorMessage: "Por favor ingrese un monto",
                pattern: validationMoney,
              }}
            />
          </IonCol>
        </IonRow>

        <FileUploader
          tag={{ type: "práctica", detail: practica! }}
          disabled={!practica && index === undefined}
        />

        <AddingSvfBtns
          label={index !== undefined ? "cambios" : "práctica"}
          action={() => {
            setAddingSvfElement(false);
          }}
        />

        <IonAlert
          isOpen={showNoEvidenceAlert}
          cssClass="docs-required-alert"
          header="Recordatorio"
          message="No adjuntaste evidencia de esta práctica. Podrá ser requerida en el futuro."
          onDidDismiss={() => setShowNoEvidenceAlert(false)}
          buttons={[
            {
              text: "Volver",
              role: "cancel",
              handler: () => setShowNoEvidenceAlert(false),
            },
            {
              text: "Guardar igual",
              handler: saveWithoutEvidence,
            },
          ]}
        />
      </IonGrid>
    </form>
  );
};

export default PracticasForm;
